import { AxiosRequestConfig } from 'axios';
import decode from 'jwt-decode';
import { AuthHeaders, LoginResponse } from './login-response.model';
import { axiosInstance, createAxiosInstance } from 'config';
import { CustomError } from 'core/errors';
import { EntitiesAPI, Entity } from 'core/entities';
import { REFRESH_KEY, TOKEN_HEADER, TOKEN_KEY } from './auth-config';
import { Roles } from './user.model';
import { ConfigMetadataResponse } from './config-metadata.model';

class AuthService {
  get token(): string {
    // Retrieves the user token from localStorage
    return localStorage.getItem(TOKEN_KEY) ?? '';
  }

  get refreshToken(): string {
    // Retrieves the user refresh_token from localStorage
    return localStorage.getItem(REFRESH_KEY) ?? '';
  }

  get loggedIn(): boolean {
    // Checks if there is a saved token and it's still valid
    return !!this.token; // handwaiving here
  }

  async login(
    username: string,
    password: string,
    options?: AxiosRequestConfig
  ): Promise<LoginResponse & { entities: Entity[] }> {
    // Get a token from api server using the fetchWithAuthorization api
    try {
      const response: AuthHeaders = await axiosInstance(
        `/sf_security/v1/sessions`,
        {
          method: 'post',
          data: {
            userName: username,
            password: btoa(password),
          },
          ...options,
        }
      );

      const loginResponse = LoginResponse.getUserData(response);
      if (!loginResponse) {
        throw CustomError.createClientError({
          technicalDescription: 'Authorization header undefined!',
        });
      }

      // save token
      this.setToken(loginResponse);

      const entitiesResponse = await EntitiesAPI.getEntities({
        params: {
          username: loginResponse.user.userName,
          isEmployee: !!loginResponse.user.roles.find(
            ({ name }) => name === Roles.FIJASTH_PY_EMPLE
          ),
        },
      });

      return { ...loginResponse, entities: entitiesResponse };
    } catch (err) {
      this.logout();
      throw err;
    }
  }

  validateToken() {
    return axiosInstance
      .get<{ response: string }>('/sf_security/v1/sessions')
      .then(({ data }) => data?.response);
  }

  isTokenExpired(token: string): boolean {
    try {
      const decoded: any = decode(token);
      if (decoded.exp < Date.now() / 1000) {
        // Checking if token is expired.
        return true;
      }
      return false;
    } catch (err) {
      console.log('expired check failed!');
      return false;
    }
  }

  setToken({ token, refreshToken }: LoginResponse): void {
    // Saves user token to localStorage
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(REFRESH_KEY, refreshToken);
  }

  tokenRefresh() {
    const { refreshToken } = this;

    return axiosInstance
      .post<LoginResponse>('/sf_security/v1/tokens', null, {
        headers: {
          [TOKEN_HEADER]: refreshToken,
          isTokenRefresh: true,
        },
      })
      .then(({ headers }) => {
        const decodedHeaders = LoginResponse.getUserData({ headers });
        if (!decodedHeaders) {
          throw new Error('No token present, token refresh failed!');
        }

        this.setToken(decodedHeaders);

        return decodedHeaders;
      });
  }

  logout(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
  }

  getMetadata() {
    const customAxios = createAxiosInstance({ baseURL: '/' });
    return customAxios
      .get<ConfigMetadataResponse>('/config-metadata')
      .then(({ data }) => {
        return data;
      });
  }
}

export default new AuthService();
